import GameObject from '../../../GameObject/GameObject';
import AnimationClip from '../AnimationClip';
import BaseCard from '../../../Prototype/Level/Card/BaseCard';
import Renderable from '../../../Component/Renderable';
import TransformComponent from '../../../Component/Transform';
import { Easing } from '@tweenjs/tween.js';
import { AwaitIterators } from '../AnimationHelper';
import TornadoCard from '../../../Prototype/Level/Card/TornadoCard';
import Tornado from '../../../Prototype/Level/Card/Tornado';
import BaseCardHandler from '../../Card/Handler/BaseCardHandler';
import CardSystem, { getCardHandler } from '../../Card/CardSystem';
import SpriteComponent from '../../../Component/Render/Sprite';
import { MathHelper } from '../../../Utility/MathHelper';
import { showCardScoreEffect } from '../CardAnimationHelper';

interface ICardTornadoMeta {
    system: CardSystem;
    sourceCard: TornadoCard;
    targets: any[];
}

export class CardTornado extends AnimationClip {
    public *execute(
        animator: GameObject,
        meta: ICardTornadoMeta
    ): IterableIterator<number> {
        animator.addChild(meta.sourceCard);

        const tornadoContainer = meta.sourceCard.find(
            'cardFrontSprite/tornadoContainer'
        );

        if (tornadoContainer && !tornadoContainer.destroyed) {
            tornadoContainer.destroy();
        }

        const tweens = [];

        for (let target of meta.targets) {
            // Add as child to animator to bring in front of overlay.
            animator.addChild(target.card);

            tweens.push(
                this.tornadoSpin(
                    animator,
                    target.card,
                    meta.sourceCard,
                    target.newValue,
                    meta.system
                )
            );
        }

        showCardScoreEffect(meta.system.scene, meta.sourceCard);

        // Fade out source card.
        const sourceCardAlphaTween = this.getTween(
            meta.sourceCard.getComponent(Renderable)
        );
        sourceCardAlphaTween.to({ alpha: 0 }, 250);
        tweens.push(this.startTween(sourceCardAlphaTween));

        yield* AwaitIterators(tweens);
    }

    private *tornadoSpin(
        animator: GameObject,
        targetCard: BaseCard,
        sourceCard: TornadoCard,
        newValue: number,
        system: CardSystem
    ): IterableIterator<number> {
        const targetCardTransform = targetCard.getComponent(TransformComponent);

        // Create tornado.
        const tornado = GameObject.Instantiate(Tornado);
        sourceCard.addChild(tornado);
        animator.addChild(tornado);

        const tornadoPositionTween = this.getTween(
            tornado.getComponent(TransformComponent).position
        );

        // Tween tornado from source card position to target card position.
        tornadoPositionTween
            .to(
                {
                    x: targetCardTransform.position.x,
                    y: targetCardTransform.position.y,
                },
                600
            )
            .easing(Easing.Quadratic.InOut);

        yield* this.startTween(tornadoPositionTween);

        // Tornado grow / fade out.
        const tornadoAlphaTween = this.getTween(
            tornado.getComponent(Renderable)
        );
        tornadoAlphaTween.to({ alpha: 0 }, 400).start(this.elapsedTime);
        const tornadoScaleTween = this.getTween(
            tornado.getComponent(TransformComponent).scale
        );
        tornadoScaleTween
            .to({ x: 2, y: 2 }, 400)
            .easing(Easing.Quadratic.In)
            .start(this.elapsedTime);

        const cardSpinElapsedTimeTween = this.getTween({
            progress: 0,
        });

        cardSpinElapsedTimeTween
            .to({ progress: 1 }, 1500)
            .easing(Easing.Quadratic.Out)
            .onUpdate(o => {
                this.setCardFlipRotation(targetCard, o.progress, 13);

                // Swap textures at 80% -- looks fine.
                if (o.progress > 0.8) {
                    targetCard
                        .getChildByName('cardFrontSprite')
                        .getComponent(
                            SpriteComponent
                        ).texture = `card${newValue + 1}`;
                }
            });

        yield* this.startTween(cardSpinElapsedTimeTween);

        showCardScoreEffect(system.scene, targetCard);

        // Destroy tornado gameobject.
        tornado.destroy();
    }

    /**
     * Set the flip rotation given a percentage of time.
     * @param card
     * @param progress
     * @param spins
     */
    private setCardFlipRotation(
        card: BaseCard,
        progress: number,
        spins: number
    ) {
        const transform: TransformComponent = card.getComponent(
            TransformComponent
        );

        const spinDuration = 1 / spins;
        const halfSpinDuration = spinDuration / 2;

        transform.scale.x =
            MathHelper.pingPong(progress + halfSpinDuration, halfSpinDuration) /
            halfSpinDuration;

        const faceUp =
            Math.floor((progress - halfSpinDuration) / spinDuration) % 2 === 0;

        const handler: BaseCardHandler = getCardHandler(card);
        handler.setCardFaceUp(faceUp);
    }
}
